import { AsyncPipe, CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Inject,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FiltersComponent } from '../../components/filters/filters.component';
import { FiltersSkeletonComponent } from '../../components/filters/filters-skeleton/filters-skeleton.component';
import { SmartFiltersComponent } from '../../components/filters/smart-filters/smart-filters.component';
import {
    SearchProgressLinearComponent
} from '../../components/search-progress-linear/search-progress-linear.component';
import { GuideBannersComponent } from '../../components/guide-banners/guide-banners.component';
import { HotelsMapPanelComponent } from '../../components/tours-map/hotels-map-panel/hotels-map-panel.component';
import { ToursMapComponent } from '../../components/tours-map/tours-map.component';
import { ToursSkeletonComponent } from '../../components/tours/tours-skeleton/tours-skeleton.component';
import { ToursNotFoundComponent } from '../../components/tours/tours-not-found/tours-not-found.component';
import { ToursComponent } from '../../components/tours/tours.component';
import { SearchStartErrorComponent } from '../../components/tours/search-start-error/search-start-error.component';
import { AppConfig } from '../../config/app.config';
import { HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig } from '../../config/hotel-tours-list-page.config';
import { BrandList, HTTPSearchResultFiltersResponse, MODE } from '../../models';
import { EventBusService } from '../../services/event-bus.service';
import { SearchService } from '../../services/search/search.service';
import { SearchProgressService } from '../../services/search-progress.service';
import { WindowRefService } from '../../services/window-ref.service';
import { BodyClassDirective } from '../../directives/body-class.directive';

type StickyFiltersMode = 'normal' | 'absolute' | 'fixed-top' | 'fixed-bottom';

@Component({
    selector: 'page-find-tours',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: environment.mobile ? './find-tours.component.mobile.html' : './find-tours.component.html',
    styleUrls: environment.mobile ? ['./find-tours.component.mobile.scss'] : ['./find-tours.component.scss'],
    imports: [
        ToursNotFoundComponent,
        ToursSkeletonComponent,
        ToursMapComponent,
        HotelsMapPanelComponent,
        ToursComponent,
        SearchStartErrorComponent,
        AsyncPipe,
        SmartFiltersComponent,
        FiltersComponent,
        FiltersSkeletonComponent,
        BodyClassDirective,
        SearchProgressLinearComponent,
        GuideBannersComponent
    ],
    standalone: true
})
export class PageFindToursComponent implements OnInit, AfterViewInit, OnDestroy {
    private static readonly filtersSkeletonMinVisibleTime = 1500;
    @ViewChild('findToursWrapper') private findToursWrapperRef: ElementRef<HTMLElement>;
    @ViewChild('filtersStickyContainer') private filtersStickyContainerRef: ElementRef<HTMLElement>;
    @ViewChild('filtersStickyInner') private filtersStickyInnerRef: ElementRef<HTMLElement>;

    private destroy$ = new Subject<void>();
    private filtersSkeletonStartedAt = 0;
    private filtersSkeletonTimer: number | null = null;
    private scrollAnimationFrameId: number | null = null;
    private stickyFiltersMode: StickyFiltersMode = 'normal';
    private lastStickyScrollY = 0;
    private readonly stickyTopOffset = 16;
    private readonly stickyBottomOffset = 16;
    private showToursLoaderSubscription: Subscription;
    private filtersResponseSubscription: Subscription;
    private searchDoneSubscription: Subscription;
    private toursNotFoundSubscription: Subscription;
    private isToursNotFound = false;
    private readonly hotTableScrollTopOffset = 16;
    private readonly onSearchStartNew = (event: CustomEvent) => {
        const detail = event.detail || {};
        const sourceFormParams = detail.formParams || this.appConfig.searchFormValues;

        if (!sourceFormParams) {
            return;
        }

        const onlyHotels: boolean = typeof detail.isOnlyHotels === 'boolean'
            ? detail.isOnlyHotels
            : Boolean(sourceFormParams.hotelsSearch);
        const formParams = { ...sourceFormParams, hotelsSearch: onlyHotels ? 1 : 0 };
        const eventIndex: number | undefined = detail.index;

        this.hasSearchStarted = true;
        this.isShowTourLoader = true;
        this.windowService.nativeWindow.lastSearchParams = formParams;
        const wrapper = this.hostRef.nativeElement.closest('.find-tours-wrapper');
        if (wrapper) {
            this.renderer.removeClass(wrapper, 'find-tours-wrapper--idle');
        }
        this.eventBus.showToursLoader();
        this.searchProgressService.start();
        this.showFiltersSkeleton();
        this.setFindToursNoResultsLayout(false);
        if (!detail.isInitialSearch) {
            this.scrollToHotTableScrollTarget();
        }

        this.searchService.startNewSearch(formParams, onlyHotels, eventIndex, detail.isInitialSearch ? 'initial' : (detail.searchTrigger || 'search_form'));
    };

    public toursNotFound$ = this.searchService.toursNotFound$;
    public searchStartFailed$ = this.searchService.searchStartFailed$;
    public showMap$ = this.eventBus.toggleMap$;
    public firstToursFound$ = this.searchService.firstToursFound$;
    public hasSearchStarted = false;
    public isShowTourLoader = true;
    public isShowFiltersSkeleton = true;
    // Отображать блок фильтров
    public isRenderFilters = true;
    // Отображать заголовок hottable
    public isRenderTitle = true;
    // Отображать карту
    public isShowMap = false;
    // Отображать лоадер ajax запросов
    public isRenderAjaxLoader = false;
    public isRenderHotelPriceCalendar = false;
    public isEnabled = true;
    public isMapAvailable = true;
    public showToursLoader$ = this.eventBus.showToursLoader$;
    messages: any = {};

    constructor(
        private searchService: SearchService,
        private searchProgressService: SearchProgressService,
        private eventBus: EventBusService,
        private cdRef: ChangeDetectorRef,
        private hostRef: ElementRef<HTMLElement>,
        private windowService: WindowRefService,
        private renderer: Renderer2,
        @Inject(HOTEL_TOURS_PAGE_CONFIG) private appConfig: HotelToursPageConfig,
    ) {
        this.hasSearchStarted = appConfig.autoStartSearch;
        this.isShowTourLoader = appConfig.autoStartSearch;
        this.isShowFiltersSkeleton = appConfig.autoStartSearch;
        this.isRenderFilters = appConfig.isRenderFilters;
        this.isRenderTitle = appConfig.isRenderTitle;
        this.isRenderAjaxLoader = appConfig.isRenderAjaxLoader;
        this.isRenderHotelPriceCalendar = this.appConfig.isRenderHotelPriceCalendar;
        this.isMapAvailable = this.appConfig.brandId !== BrandList.TICKETS_KZ;
        this.isShowMap = !this.isMapAvailable;
        this.messages = this.appConfig.translates.filters;
    }


    ngOnInit(): void {
        this.searchService.trackPageOpen(this.appConfig.searchFormValues);
        this.windowService.nativeWindow.addEventListener('search:start-new', this.onSearchStartNew as EventListener);

        if (this.appConfig.autoStartSearch && this.appConfig.searchFormValues) {
            window.setTimeout(() => {
                this.windowService.nativeWindow.dispatchEvent(new CustomEvent('search:start-new', {
                    detail: {
                        formParams: this.appConfig.searchFormValues,
                        isOnlyHotels: Boolean(this.appConfig.searchFormValues.hotelsSearch),
                        isInitialSearch: true,
                    },
                }));
            }, 0);
        }

        this.toggleAngularHotTableView(true);

        this.eventBus.toggleMap$.subscribe((isMapOpen) => {
            if (!this.isMapAvailable) {
                return;
            }

            this.isShowMap = !isMapOpen;
            this.cdRef.detectChanges();
        });

        this.showToursLoaderSubscription = this.showToursLoader$.subscribe((p) => {
            this.isShowTourLoader = p;
            this.updateFindToursNoResultsLayout();
            this.cdRef.detectChanges();
        });

        this.toursNotFoundSubscription = this.toursNotFound$.subscribe((isNotFound) => {
            this.isToursNotFound = Boolean(isNotFound);
            this.updateFindToursNoResultsLayout();
        });

        this.filtersResponseSubscription = this.searchService.filters$.subscribe((response) => {
            if (this.hasFiltersData(response)) {
                this.hideFiltersSkeletonWhenReady();
            }
        });

        this.searchDoneSubscription = this.searchService.searchIsDone$.subscribe(() => {
            this.searchProgressService.end();
            this.windowService.nativeWindow.requestAnimationFrame(() => this.updateStickyFilters());
        });
    }

    ngAfterViewInit(): void {
        if (environment.mobile) {
            return;
        }

        this.lastStickyScrollY = this.windowService.nativeWindow.scrollY || 0;
        this.windowService.nativeWindow.requestAnimationFrame(() => this.updateStickyFilters());
    }

    @HostListener('window:scroll')
    onWindowScroll(): void {
        this.updateStickyFilters();
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.resetStickyFilters();
        this.windowService.nativeWindow.requestAnimationFrame(() => this.updateStickyFilters());
    }

    ngOnDestroy(): void {
        this.windowService.nativeWindow.removeEventListener('search:start-new', this.onSearchStartNew as EventListener);
        this.showToursLoaderSubscription?.unsubscribe();
        this.filtersResponseSubscription?.unsubscribe();
        this.searchDoneSubscription?.unsubscribe();
        this.toursNotFoundSubscription?.unsubscribe();
        this.clearFiltersSkeletonTimer();
        this.cancelScrollAnimation();
        this.setFindToursNoResultsLayout(false);
        this.resetStickyFilters();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private toggleAngularHotTableView(show: boolean) {
        // После запуска приложения при поиске сразу открываем
        const angularHotTable = document.getElementById('angularHottable');
        if (angularHotTable) {
            angularHotTable.classList.add('init');
            if (this.appConfig.mode === MODE.MODE_SEARCH && show) {
                angularHotTable.style.display = 'block';
            }
        }
    }

    private scrollToHotTableScrollTarget(): void {
        const target = this.windowService.nativeWindow.document.getElementById('ng-hot-table-scroll-target');
        if (!target) {
            return;
        }

        const targetTop = target.getBoundingClientRect().top
            + this.windowService.nativeWindow.scrollY
            - this.hotTableScrollTopOffset;
        this.animateScrollTo(Math.max(0, targetTop));
    }

    private animateScrollTo(targetTop: number): void {
        this.cancelScrollAnimation();

        const win = this.windowService.nativeWindow;
        const startTop = win.scrollY || 0;
        const distance = targetTop - startTop;

        if (Math.abs(distance) < 4) {
            win.scrollTo(0, targetTop);
            return;
        }

        const duration = 900;
        const startTime = win.performance.now();
        const easeInOutCubic = (progress: number): number => {
            return progress < 0.5
                ? 4 * progress * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 3) / 2;
        };

        const step = (currentTime: number): void => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeInOutCubic(progress);

            win.scrollTo(0, startTop + distance * easedProgress);

            if (progress < 1) {
                this.scrollAnimationFrameId = win.requestAnimationFrame(step);
                return;
            }

            this.scrollAnimationFrameId = null;
        };

        this.scrollAnimationFrameId = win.requestAnimationFrame(step);
    }

    private cancelScrollAnimation(): void {
        if (this.scrollAnimationFrameId === null) {
            return;
        }

        this.windowService.nativeWindow.cancelAnimationFrame(this.scrollAnimationFrameId);
        this.scrollAnimationFrameId = null;
    }

    private showFiltersSkeleton(): void {
        this.clearFiltersSkeletonTimer();
        this.filtersSkeletonStartedAt = Date.now();
        this.isShowFiltersSkeleton = true;
        this.cdRef.detectChanges();
    }

    private hideFiltersSkeletonWhenReady(): void {
        this.clearFiltersSkeletonTimer();

        if (!this.isShowFiltersSkeleton) {
            return;
        }

        const timePassed = Date.now() - this.filtersSkeletonStartedAt;
        const timeout = PageFindToursComponent.filtersSkeletonMinVisibleTime - timePassed;

        if (timeout <= 0) {
            this.isShowFiltersSkeleton = false;
            this.cdRef.detectChanges();
            this.windowService.nativeWindow.requestAnimationFrame(() => this.updateStickyFilters());
            return;
        }

        this.filtersSkeletonTimer = this.windowService.nativeWindow.setTimeout(() => {
            this.isShowFiltersSkeleton = false;
            this.filtersSkeletonTimer = null;
            this.cdRef.detectChanges();
            this.updateStickyFilters();
        }, timeout);
    }

    private clearFiltersSkeletonTimer(): void {
        if (this.filtersSkeletonTimer === null) {
            return;
        }

        this.windowService.nativeWindow.clearTimeout(this.filtersSkeletonTimer);
        this.filtersSkeletonTimer = null;
    }

    private hasFiltersData(response: HTTPSearchResultFiltersResponse): boolean {
        return Array.isArray(response?.filters) && response.filters.length > 0;
    }

    private updateFindToursNoResultsLayout(): void {
        if (environment.mobile) {
            return;
        }

        this.setFindToursNoResultsLayout(this.isToursNotFound && !this.isShowTourLoader);
    }

    private setFindToursNoResultsLayout(isNoResults: boolean): void {
        if (environment.mobile) {
            return;
        }

        const wrapper = this.hostRef.nativeElement.closest('.find-tours-wrapper');
        if (!wrapper) {
            return;
        }

        if (isNoResults) {
            this.renderer.addClass(wrapper, 'find-tours-wrapper--not-found');
            return;
        }

        this.renderer.removeClass(wrapper, 'find-tours-wrapper--not-found');
    }

    private updateStickyFilters(): void {
        if (environment.mobile) {
            return;
        }

        const wrapper = this.findToursWrapperRef?.nativeElement;
        const container = this.filtersStickyContainerRef?.nativeElement;
        const filters = this.filtersStickyInnerRef?.nativeElement;

        if (!wrapper || !container || !filters) {
            return;
        }

        const win = this.windowService.nativeWindow;
        const scrollY = win.scrollY || 0;
        const viewportHeight = win.innerHeight || 0;
        const wrapperBottom = wrapper.getBoundingClientRect().top + scrollY + wrapper.offsetHeight;
        const containerTop = container.getBoundingClientRect().top + scrollY;
        const filtersHeight = filters.offsetHeight;

        if (filtersHeight <= 0 || wrapper.offsetHeight <= filtersHeight) {
            this.resetStickyFilters();
            this.lastStickyScrollY = scrollY;
            return;
        }

        this.renderer.setStyle(container, 'height', `${filtersHeight}px`);
        this.renderer.setStyle(filters, 'width', `${container.offsetWidth}px`);

        const maxDocumentTop = wrapperBottom - filtersHeight;
        const isScrollingDown = scrollY > this.lastStickyScrollY;
        const isShortEnoughForViewport = filtersHeight <= viewportHeight - this.stickyTopOffset - this.stickyBottomOffset;

        if (scrollY + this.stickyTopOffset <= containerTop) {
            this.resetStickyFilters();
            this.lastStickyScrollY = scrollY;
            return;
        }

        if (isShortEnoughForViewport) {
            const fixedTopDocumentTop = scrollY + this.stickyTopOffset;
            if (fixedTopDocumentTop >= maxDocumentTop) {
                this.positionFiltersAbsolute(maxDocumentTop - containerTop);
            } else {
                this.stickFiltersToViewport('top', container);
            }
            this.lastStickyScrollY = scrollY;
            return;
        }

        if (isScrollingDown && this.stickyFiltersMode === 'fixed-top') {
            this.releaseFiltersAtCurrentPosition(scrollY, containerTop);
        } else if (!isScrollingDown && this.stickyFiltersMode === 'fixed-bottom') {
            this.releaseFiltersAtCurrentPosition(scrollY, containerTop);
        }

        const currentDocumentTop = this.getFiltersDocumentTop(scrollY, containerTop);
        const bottomStickDocumentTop = scrollY + viewportHeight - this.stickyBottomOffset - filtersHeight;
        const topStickDocumentTop = scrollY + this.stickyTopOffset;

        if (isScrollingDown) {
            if (bottomStickDocumentTop >= maxDocumentTop) {
                this.positionFiltersAbsolute(maxDocumentTop - containerTop);
            } else if (currentDocumentTop + filtersHeight <= scrollY + viewportHeight - this.stickyBottomOffset) {
                this.stickFiltersToViewport('bottom', container);
            }
        } else if (topStickDocumentTop <= containerTop) {
            this.resetStickyFilters();
        } else if (currentDocumentTop >= topStickDocumentTop) {
            this.stickFiltersToViewport('top', container);
        }

        this.lastStickyScrollY = scrollY;
    }

    private getFiltersDocumentTop(scrollY: number, containerTop: number): number {
        const filters = this.filtersStickyInnerRef.nativeElement;

        if (this.stickyFiltersMode === 'fixed-top') {
            return scrollY + this.stickyTopOffset;
        }

        if (this.stickyFiltersMode === 'fixed-bottom') {
            return scrollY + this.windowService.nativeWindow.innerHeight - this.stickyBottomOffset - filters.offsetHeight;
        }

        if (this.stickyFiltersMode === 'absolute') {
            return containerTop + Number.parseFloat(filters.style.top || '0');
        }

        return containerTop;
    }

    private releaseFiltersAtCurrentPosition(scrollY: number, containerTop: number): void {
        const currentDocumentTop = this.getFiltersDocumentTop(scrollY, containerTop);
        this.positionFiltersAbsolute(currentDocumentTop - containerTop);
    }

    private stickFiltersToViewport(edge: 'top' | 'bottom', container: HTMLElement): void {
        const filters = this.filtersStickyInnerRef.nativeElement;

        this.clearStickyPositionClasses();
        this.renderer.addClass(filters, 'is-sticky-fixed');
        this.renderer.setStyle(filters, 'left', `${container.getBoundingClientRect().left}px`);
        this.renderer.removeStyle(filters, 'top');
        this.renderer.removeStyle(filters, 'bottom');

        if (edge === 'top') {
            this.renderer.setStyle(filters, 'top', `${this.stickyTopOffset}px`);
            this.stickyFiltersMode = 'fixed-top';
            return;
        }

        this.renderer.setStyle(filters, 'bottom', `${this.stickyBottomOffset}px`);
        this.stickyFiltersMode = 'fixed-bottom';
    }

    private positionFiltersAbsolute(top: number): void {
        const filters = this.filtersStickyInnerRef.nativeElement;

        this.clearStickyPositionClasses();
        this.renderer.addClass(filters, 'is-sticky-absolute');
        this.renderer.setStyle(filters, 'top', `${Math.max(0, top)}px`);
        this.renderer.removeStyle(filters, 'bottom');
        this.renderer.removeStyle(filters, 'left');
        this.stickyFiltersMode = 'absolute';
    }

    private resetStickyFilters(): void {
        const filters = this.filtersStickyInnerRef?.nativeElement;
        const container = this.filtersStickyContainerRef?.nativeElement;

        if (!filters || !container) {
            return;
        }

        this.clearStickyPositionClasses();
        this.renderer.removeStyle(filters, 'top');
        this.renderer.removeStyle(filters, 'bottom');
        this.renderer.removeStyle(filters, 'left');
        this.renderer.removeStyle(filters, 'width');
        this.renderer.removeStyle(container, 'height');
        this.stickyFiltersMode = 'normal';
    }

    private clearStickyPositionClasses(): void {
        const filters = this.filtersStickyInnerRef?.nativeElement;

        if (!filters) {
            return;
        }

        this.renderer.removeClass(filters, 'is-sticky-fixed');
        this.renderer.removeClass(filters, 'is-sticky-absolute');
    }
}
